import { Component, Prop, Vue } from "vue-facing-decorator";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as EsriLeafletVector from "esri-leaflet-vector";

import { useGlobalStore } from "@/stores/store";
import { watch } from "vue";
// import { Dataset } from "@/models/dataset";

import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw"; // Extends L with draw methods

import "maplibre-gl-leaflet/leaflet-maplibre-gl";
// import "maplibre-gl-leaflet/leaflet-maplibre-gl.css"; // Uncomment if you use MapLibre GL for basemaps

@Component({
    name: "HeroMap",
    emits: ["rectangle-drawn", "editing-enabled", "editing-disabled"],
})
export default class HeroMap extends Vue {
    //   @Prop({ type: Array, required: true }) bounds!: L.LatLngBoundsLiteral;

    // Required Esri ApiKey to load basemaps. Now is a free one, has to be modified with an in-house one
    private apiKey = "AAPK801ff006f06349cdb9d6645cdc35517akJ-3qtcpxxfpuc8WxUSTZjnqiDlhitvFlk4JkFWMK789oMb9xyC7Kd4hUgZ_xqmC";

    private map!: L.Map;
    // private bboxesLayerGroup!: L.bboxesLayerGroup;
    private bboxesLayerGroup!: L.FeatureGroup;
    private rectangle: L.Rectangle | null = null;

    private temporaryPolygon: L.Rectangle | null = null;
    // private rectangle!: L.Rectangle;

    private store = useGlobalStore;

    private resizeObserver!: ResizeObserver;
    private mapContainer!: HTMLElement;

    private readonly topBuffer = 80; // Top padding for search bar. The value of 80 was manually fine-tuned and depends on the mapPageBounds!

    private readonly rectangleColor = "#0071BC"; // Rectangle color
    private readonly rectangleWeight = 2; // Rectangle border weight

    private currentBasemapName: string = "ArcGIS:LightGray";

    // // Austria bounds
    // private mapPageBounds: L.LatLngBoundsExpression = [
    //     [46.7723, 9.5309],   // SW (bottom-left buffer)
    //     [49.0205, 17.1606],     // NE (top-right buffer)
    // ];

    // Bigger Bbox from datasets
    private mapPageBounds: L.LatLngBoundsExpression = [
        [46.0342, 9.1735], // SW (bottom-left buffer)
        [49.2343, 17.4572], // NE (top-right buffer)
    ];

    // Bigger Bbox from datasets
    private mapPageBoundsAlternative: L.LatLngBoundsExpression = [
        [45.9842, 9.1406], // SW (bottom-left buffer)
        [49.2486, 17.4902], // NE (top-right buffer)
    ];

    private currentBasemapLayer!: L.TileLayer;
    private basemapLayers: Record<string, L.TileLayer> = {};

    /**
     * Lifecycle hook called when the component is mounted.
     * Initializes the Leaflet map, sets up base layers, and adds either a circle or rectangle
     * based on the `bounds` prop passed to the component.
     */
    mounted() {
        // Grab the DOM element via ref
        this.mapContainer = this.$refs.mapContainer as HTMLElement;

        this.map = L.map(this.mapContainer, {
            zoomControl: false, // Enable scroll wheel zoom only if isMapPage is true
            scrollWheelZoom: this.isMapPage, // Enable scroll wheel zoom only if isMapPage is true
            doubleClickZoom: this.isMapPage, // Enable scroll wheel zoom only if isMapPage is true
            touchZoom: this.isMapPage, // Enable scroll wheel zoom only if isMapPage is true
            dragging: this.isMapPage, // Disable dragging for the map
            maxBoundsViscosity: 1.0,
        });

        this.bboxesLayerGroup = L.featureGroup().addTo(this.map);

        // EsriLeafletVector.vectorBasemapLayer("ArcGIS:LightGray", {
        //     apiKey: this.apiKey, // ← Optional if you want higher usage limits than the free tier
        // }).addTo(this.map);

        const martinStyleUrl = "https://gis.geosphere.at/base/ts/style/data-viz-white";
        
        this.basemapLayers = {
            "ArcGIS:LightGray": EsriLeafletVector.vectorBasemapLayer("ArcGIS:LightGray", {
                apiKey: this.apiKey
            }),
            "Basemap.at": L.tileLayer('https://mapsneu.wien.gv.at/basemap/geolandbasemap/normal/google3857/{z}/{y}/{x}.png', {
                attribution: '<a href="https://www.basemap.at" target="_blank">basemap.at</a>',
                noWrap: true,
                subdomains: ['', '1', '2', '3', '4']
            }),
            "ArcGIS:Imagery": L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles © <a href="https://www.esri.com/" target="_blank">Esri</a> — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community'
            }),
            // "ESRI Topo": L.tileLayer('https://server.arcgisonline.com/arcgis/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}', {
            //     attribution: 'Tiles © <a href="https://www.esri.com/" target="_blank">Esri</a> — Source: Esri, USGS, NOAA'
            // })
            "OpenStreetMap": L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }),
            "GeoSphere": (L as any).maplibreGL({
                style: martinStyleUrl
                // attribution: '';
                // attribution: '<a href="https://github.com/protomaps/basemaps">Protomaps</a> © <a href="https://openstreetmap.org">OpenStreetMap</a>',
            })
        };

        this.currentBasemapLayer = this.basemapLayers["ArcGIS:LightGray"];
        this.currentBasemapLayer.addTo(this.map);


        // Initialize layer group for polygons
        // this.bboxesLayerGroup = L.bboxesLayerGroup().addTo(this.map);
        this.bboxesLayerGroup = L.featureGroup().addTo(this.map);

        this.drawPolygons(this.store.resultsForMap); // initial render

        // Add a click event listener to the map
        // this.map.on("click", (e: L.LeafletMouseEvent) => {
        //     console.log("Map clicked at:", e.latlng);
        // });

        // Watch store.results for changes and draw polygons
        watch(
            () => this.store.resultsForMap,
            (newResults) => {
                this.drawPolygons(newResults);
            },
            { deep: true },
        );

        watch(
            () => this.store.selectionBox,
            (newGeometry) => {
                this.drawRectangle(newGeometry);
            },
            { deep: true },
        );

        // Watch container resize to re-fit map
        this.resizeObserver = new ResizeObserver(() => {
            // console.log("Resize observer triggered");

            if (this.map) {
                this.map.invalidateSize();
                if (this.isMapPage) {
                    this.map.fitBounds(this.mapPageBounds);
                    // console.log("Map page. Resize after slide");
                } else {
                    // this.map.fitBounds(this.responsiveAustriaBounds);
                    // this.map.fitBounds(this.mapPageBounds);

                    this.fitBoundsWithSearchPadding(this.mapPageBounds);

                    // console.log("NO map page. Resize responsive");
                }
            }
        });

        this.resizeObserver.observe(this.mapContainer);

        if (this.isMapPage) {
            this.addCustomControls();
            this.addBasemapSwitcher();
        }

        this.map.attributionControl.setPrefix(false);

    }

    public drawTemporaryPolygon(bbox: { xmin: number; ymin: number; xmax: number; ymax: number } | null): void {
        // console.log("drawTemporaryPolygon");

        // if (!this.map) {
        //     console.error("Map is not initialized yet.");
        //     return;
        // }

        // if (this.temporaryPolygon) {
        //     this.temporaryPolygon.remove(); // Remove the existing polygon
        // }

        // "tethys-blue1-dark": "#0071BC",
        // "tethys-blue1-light": "#E0F0FA",
        // "tethys-blue2-dark": "#00B5DE",
        // "tethys-blue2-light": "#E0F4FA",
        // "tethys-green-dark": "#68D7CF",
        // "tethys-green-light": "#BBEDE9",

        if (bbox) {
            const bounds: L.LatLngBoundsExpression = [
                [bbox.ymin, bbox.xmin],
                [bbox.ymax, bbox.xmax],
            ];

            try {
                this.temporaryPolygon = L.rectangle(bounds, {
                    color: this.getRectangleSettings()[0], // color for hover effect
                    weight: 1,
                    fillOpacity: 0.1,
                }).addTo(this.bboxesLayerGroup);
            } catch (error) {
                console.error("Error adding temporary polygon to the map:", error);
            }
        } else {
            console.warn("Invalid bbox provided to drawTemporaryPolygon:", bbox);
        }
    }

    private clearTemporaryPolygon(): void {
        // console.log("clearTemporaryPolygon!!!!");

        if (this.temporaryPolygon) {
            this.temporaryPolygon.remove(); // Remove the existing polygon
            this.temporaryPolygon = null;
        }
    }

    private isValidBbox(bbox: { xmin: number; ymin: number; xmax: number; ymax: number }): boolean {
        return (
            typeof bbox.xmin === "number" &&
            typeof bbox.ymin === "number" &&
            typeof bbox.xmax === "number" &&
            typeof bbox.ymax === "number" &&
            bbox.xmin < bbox.xmax &&
            bbox.ymin < bbox.ymax
        );
    }

    /**
     * Clear existing polygons and draw rectangles from dataset bounding boxes.
     */
    private drawPolygons(datasets: Array<{ id: number; bbox_xmin: string; bbox_xmax: string; bbox_ymin: string; bbox_ymax: string }>) {
        this.bboxesLayerGroup.clearLayers();

        datasets.forEach((dataset) => {
            const { bbox_xmin, bbox_xmax, bbox_ymin, bbox_ymax } = dataset;

            if (bbox_xmin && bbox_xmax && bbox_ymin && bbox_ymax) {
                const bounds: L.LatLngBoundsExpression = [
                    [parseFloat(bbox_ymin), parseFloat(bbox_xmin)],
                    [parseFloat(bbox_ymax), parseFloat(bbox_xmax)],
                ];
                const polygon = L.rectangle(bounds, {
                    color: "#00B5DE", // Border color
                    opacity: 0.5, // Border opacity
                    weight: 0.5, // Border thickness
                    fillColor: "#00B5DE", // Interior color
                    fillOpacity: 0.05, // Interior opacity
                });

                if (this.isMapPage) {
                    polygon.on("click", () => {
                        console.log("Polygon clicked:", dataset.id);
                    });
                } else {
                    polygon.setStyle({ interactive: false });
                }

                polygon.addTo(this.bboxesLayerGroup);
            }
        });
    }

    // private makeRectangleEditable(): void {
    //     if (!this.rectangle) return;

    //     // Enable dragging of the entire rectangle
    //     (this.rectangle as any).dragging?.enable?.();

    //     // Enable resizing by adding event handlers on 'mousedown' or similar
    //     // You could also integrate Leaflet.Draw for a full resizable UI later
    //     this.rectangle.on("edit", () => {
    //         const bounds = this.rectangle!.getBounds();
    //         const sw = bounds.getSouthWest();
    //         const ne = bounds.getNorthEast();

    //         const updatedBbox = {
    //             xmin: sw.lng,
    //             ymin: sw.lat,
    //             xmax: ne.lng,
    //             ymax: ne.lat,
    //         };

    //         this.$emit("rectangle-drawn", updatedBbox);
    //     });
    // }

    // private disableRectangleEditing(): void {
    //     if (!this.rectangle) return;
    //     (this.rectangle as any).editing.disable();
    // }

    private enableRectangleEditing(): void {
        if (!this.rectangle) return;

        // Enable editing using Leaflet Draw
        (this.rectangle as any).editing.enable();
        this.$emit("editing-enabled");

        // Optionally, listen to edit events to sync the store or emit updated bbox
        this.rectangle.on("edit", () => {
            const bounds = this.rectangle!.getBounds();
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();

            const updatedBbox = {
                xmin: sw.lng,
                ymin: sw.lat,
                xmax: ne.lng,
                ymax: ne.lat,
            };

            this.$emit("rectangle-drawn", updatedBbox);
            this.$emit("editing-disabled");
        });
    }

    private drawRectangle(newGeometry: { xmin: number; ymin: number; xmax: number; ymax: number } | null) {
        this.rectangle?.remove(); // Remove existing rectangle if any

        if (newGeometry) {
            // console.log("New geometry:", newGeometry);
            const bounds: L.LatLngBoundsExpression = [
                [newGeometry.ymin, newGeometry.xmin],
                [newGeometry.ymax, newGeometry.xmax],
            ];

            this.rectangle = L.rectangle(bounds, {
                color: this.getRectangleSettings()[0],
                weight: this.getRectangleSettings()[1],
                fillOpacity: 0,
            }).addTo(this.map);
        } else {
            this.rectangle = null; // Fully clear it so mousemove works properly next time
        }
    }

    /**
     * Clean up resize observer on component unmount.
     */
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    get isMapPage(): boolean {
        return this.$route.name === "Map";
    }

    // get responsiveAustriaBounds(): L.LatLngBoundsExpression {
    //     const screenHeight = window.innerHeight;
    //     return screenHeight < 750
    //         ? this.austriaBoundsShortScreen
    //         : this.austriaBoundsDefault;
    // }

    /**
     * Fit map bounds with top padding based on search bar height.
     */
    private fitBoundsWithSearchPadding(bounds: L.LatLngBoundsExpression) {
        const searchBar = document.getElementById("search-bar");
        const topPadding = searchBar?.offsetHeight ?? 0;
        // console.log("Top padding for search bar:", topPadding);

        this.map.fitBounds(bounds, {
            paddingTopLeft: [0, topPadding - this.topBuffer],
            paddingBottomRight: [0, 0],
        });
    }

    /**
     * Adds custom map controls to the bottom-right corner.
     */
    private addCustomControls(): void {
        const controlDiv = L.DomUtil.create("div");

        // 🔴 Prevent click/scroll events from reaching the map
        L.DomEvent.disableClickPropagation(controlDiv);
        L.DomEvent.disableScrollPropagation(controlDiv);

        controlDiv.innerHTML = `
        <div class="flex flex-col gap-2 border-transparent p-2">
            <button title="Zoom In" class="w-10 h-10 flex items-center justify-center rounded drop-shadow bg-white px-0.5 py-2 hover:shadow-[inset_0_0_0_1px_#0071BC] focus:shadow-[inset_0_0_0_2px_#0071BC] focus-visible:outline-none" data-tool="zoom-in"><i class="fas fa-plus"></i></button>
            <button title="Zoom Out" class="w-10 h-10 flex items-center justify-center rounded drop-shadow bg-white px-0.5 py-2 hover:shadow-[inset_0_0_0_1px_#0071BC] focus:shadow-[inset_0_0_0_2px_#0071BC] focus-visible:outline-none" data-tool="zoom-out"><i class="fas fa-minus"></i></button>
            <button title="Select drawing a rectangle" class="w-10 h-10 flex items-center justify-center rounded drop-shadow bg-white px-0.5 py-2 hover:shadow-[inset_0_0_0_1px_#0071BC] focus:shadow-[inset_0_0_0_2px_#0071BC] focus-visible:outline-none" data-tool="draw-rectangle"><i class="fa-solid fa-crop-simple"></i></button>
            <button title="Select based on the visible area" class="w-10 h-10 flex items-center justify-center rounded drop-shadow bg-white px-0.5 py-2 hover:shadow-[inset_0_0_0_1px_#0071BC] focus:shadow-[inset_0_0_0_2px_#0071BC] focus-visible:outline-none" data-tool="mapExtent"><i class="fa-solid fa-desktop"></i></button>
            <button title="Select around your current location" class="w-10 h-10 flex items-center justify-center rounded drop-shadow bg-white px-0.5 py-2 hover:shadow-[inset_0_0_0_1px_#0071BC] focus:shadow-[inset_0_0_0_2px_#0071BC] focus-visible:outline-none" data-tool="location"><i class="fa-solid fa-location-crosshairs"></i></button>
        </div>
        `;

        const control = L.Control.extend({
            options: { position: "bottomright" },
            onAdd: () => controlDiv,
        });

        this.map.addControl(new control());

        // Attach handlers
        controlDiv.addEventListener("click", (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest("button[data-tool]");
            if (!target) return;

            const tool = target.getAttribute("data-tool");
            if (!tool) return;

            switch (tool) {
                case "zoom-in":
                    // this.clearMapEventListeners();
                    // console.log("Zooming in");

                    this.map.zoomIn();
                    break;
                case "zoom-out":
                    // this.clearMapEventListeners();
                    // console.log("Zooming out");

                    this.map.zoomOut();
                    break;
                case "draw-rectangle":
                    // this.clearMapEventListeners();
                    // console.log("Drawing rectangle");

                    this.selectByRectangle();
                    break;
                case "location":
                    // this.clearMapEventListeners();
                    this.selectByCurrentLocation();
                    break;
                case "mapExtent":
                    // this.clearMapEventListeners();
                    this.selectByCurrentMapExtent();
                    break;
                // case "basemap":
                //     // this.clearMapEventListeners();
                //     this.toggleBasemap();
                //     break;
            }
        });
    }

    private addBasemapSwitcher(): void {
        const container = L.DomUtil.create("div");

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        container.innerHTML = `
            <div class="relative left-[10px] bottom-[20px]">
                <button 
                    title="Change Basemap"
                    class="w-10 h-10 flex items-center justify-center rounded drop-shadow bg-white px-0.5 py-2 hover:shadow-[inset_0_0_0_1px_#0071BC] focus:shadow-[inset_0_0_0_2px_#0071BC] focus-visible:outline-none"
                    id="basemap-toggle-btn">
                    <i class="fa-solid fa-layer-group"></i>
                </button>
                    <div 
                        id="basemap-menu"
                        class="absolute w-32 left-0 bottom-12 flex-col gap-1 bg-white shadow-lg border rounded p-2 hidden z-10 text-left"
                    >
                        <button class="text-sm hover:text-[#0071BC] hover:underline" data-basemap="ArcGIS:LightGray">ArcGIS LightGray</button>
                        <button class="text-sm hover:text-[#0071BC] hover:underline" data-basemap="Basemap.at">Basemap.at</button>
                        <button class="text-sm hover:text-[#0071BC] hover:underline" data-basemap="ArcGIS:Imagery">ArcGIS Imagery</button>
                        <button class="text-sm hover:text-[#0071BC] hover:underline" data-basemap="OpenStreetMap">OpenStreetMap</button>
                        <button class="text-sm hover:text-[#0071BC] hover:underline" data-basemap="GeoSphere">GeoSphere</button>
                    </div>
            </div>
        `;

        const control = L.Control.extend({
            options: { position: "bottomleft" },
            onAdd: () => container,
        });

        this.map.addControl(new control());

        const toggleBtn = container.querySelector("#basemap-toggle-btn") as HTMLButtonElement;
        const menu = container.querySelector("#basemap-menu") as HTMLElement;

        toggleBtn.addEventListener("click", () => {
            menu.classList.toggle("hidden");
        });

        menu.querySelectorAll("button[data-basemap]").forEach((btn) => {
            btn.addEventListener("click", (event) => {
                const name = (event.currentTarget as HTMLElement).getAttribute("data-basemap");
                if (!name || !(name in this.basemapLayers)) return;

                // Remove current layer
                this.map.removeLayer(this.currentBasemapLayer);
                // Add new layer
                this.currentBasemapLayer = this.basemapLayers[name];
                this.currentBasemapLayer.addTo(this.map);

                // Set the current basemap name
                this.currentBasemapName = name;

                // Update rectangle width if it exists
                if (this.rectangle) {
                    this.rectangle.setStyle({ color: this.getRectangleSettings()[0], weight: this.getRectangleSettings()[1] });
                }

                // Reset attribution before adding new one ---
                this.map.attributionControl.setPrefix(false);

                // Remove all attributions by iterating over current ones
                // @ts-ignore: access private property for reading only
                const currentAttributions = Object.keys((this.map.attributionControl as any)._attributions || {});
                currentAttributions.forEach(attr => {
                    this.map.attributionControl.removeAttribution(attr);
                });

                // Add correct attribution for the selected basemap
                if (name === "GeoSphere") {
                    console.log("Adding GeoSphere attribution");
                    
                    this.map.attributionControl.addAttribution(
                        '<a href="https://github.com/protomaps/basemaps">Protomaps</a> © <a href="https://openstreetmap.org">OpenStreetMap</a>'
                    );
                } else {
                    console.log("Adding attribution for:", name);
                    
                    // Add the attribution from the layer options if available
                    const attribution = (this.currentBasemapLayer as any).options?.attribution;
                    if (attribution) {
                        this.map.attributionControl.addAttribution(attribution);
                    }
                }
                // Hide menu after selection
                menu.classList.add("hidden");

                // Wait a short delay to let Leaflet/plugin update attribution
                setTimeout(() => {
                    this.sanitizeAttribution();
                }, 3000);

            });
        });
    }

    private sanitizeAttribution(): void {
        const attrControl = document.querySelector(".leaflet-control-attribution");
        if (!attrControl) return;

        // Temporary bug fix!: Remove the unwanted native code text added by MapLibre GL (library bug?)
        attrControl.innerHTML = attrControl.innerHTML.replace(/,\s*function\s*\(\)\s*\{\s*\[native code\]\s*\}/, "");
    }

    // private clearMapEventListeners(): void {
    //     this.map.off("mousedown");
    //     this.map.off("mousemove");
    //     this.map.off("mouseup");
    // }

    private getRectangleSettings(): [string, number] {
        // switch (this.currentBasemapName) {
        //     case "ArcGIS:LightGray":
        //         return "#0071BC"; // blue
        //     case "Basemap.at":
        //         return "#D97706"; // orange
        //     case "ArcGIS:Imagery":
        //         return "#00B5DE"; // cyan
        //     case "OpenStreetMap":
        //         return "#22C55E"; // green
        //     default:
        //         return "#0071BC";
        // }
        switch (this.currentBasemapName) {
            case "ArcGIS:Imagery":
                return ["#fff", 3];
            default:
                return [this.rectangleColor, this.rectangleWeight];
        }
    }

    /**
     * Allows the user to draw a rectangle by dragging on the map.
     * The rectangle is removed immediately after drawing, and the BBOX is emitted for further use.
     */
    private selectByRectangle(): void {
        let startLatLng: L.LatLng | null = null;

        const onMouseDown = (e: L.LeafletMouseEvent) => {
            // Remove any already existing selection rectangle
            // this.removeSelectionRectangle();
            // console.log("mousedown");

            startLatLng = e.latlng;

            // Disable dragging while drawing to prevent map movement
            this.map.dragging.disable();

            const onMouseMove = (moveEvent: L.LeafletMouseEvent) => {
                // console.log("mousemove");

                const endLatLng = moveEvent.latlng;

                const bounds: L.LatLngBoundsExpression = [
                    [startLatLng!.lat, startLatLng!.lng],
                    [endLatLng.lat, endLatLng.lng],
                ];

                if (this.rectangle) {
                    // console.log("if");

                    this.rectangle.setBounds(bounds);
                } else {
                    // console.log("else");

                    this.rectangle = L.rectangle(bounds, {
                        color: this.getRectangleSettings()[0],
                        weight: this.getRectangleSettings()[1],
                        fillOpacity: 0,
                    }).addTo(this.map);
                }
            };

            const onMouseUp = (upEvent: L.LeafletMouseEvent) => {
                // console.log("mouseup");

                const endLatLng = upEvent.latlng;
                const bounds = L.latLngBounds(startLatLng!, endLatLng);

                const sw = bounds.getSouthWest();
                const ne = bounds.getNorthEast();

                const bbox = {
                    xmin: sw.lng,
                    ymin: sw.lat,
                    xmax: ne.lng,
                    ymax: ne.lat,
                };

                // Emit bbox for use in OpenSearch query
                this.$emit("rectangle-drawn", bbox);

                // Re-enable dragging after drawing
                this.map.dragging.enable();

                //  listeners
                this.map.off("mousemove", onMouseMove);
                this.map.off("mouseup", onMouseUp);
            };

            this.map.on("mousemove", onMouseMove);
            this.map.on("mouseup", onMouseUp);
        };

        // Only activate once per click
        this.map.once("mousedown", onMouseDown);
    }

    /**
     * Selects a 10km x 10km bounding box centered on the user's current location
     * and emits it as a selection rectangle.
     */
    private selectByCurrentLocation(): void {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                const halfSideKm = 25; // e.g. 10km side → 5km in each direction

                const latOffset = halfSideKm / 111.32; // degrees per km
                const lngOffset = halfSideKm / (111.32 * Math.cos((lat * Math.PI) / 180));

                const bbox = {
                    xmin: lng - lngOffset,
                    ymin: lat - latOffset,
                    xmax: lng + lngOffset,
                    ymax: lat + latOffset,
                };

                this.$emit("rectangle-drawn", bbox);

                // Draw rectangle visually
                this.drawRectangle(bbox);

                // Center map on user
                this.map.setView([lat, lng], 10);
            },
            (error) => {
                alert("Could not get your location: " + error.message);
            },
            {
                enableHighAccuracy: true,
            },
        );
    }

    /**
     * Toggles the basemap between options.
     * Currently, it only logs a message to the console.
     */
    private toggleBasemap(): void {
        console.log("Basemap toggling not implemented yet.");

        this.enableRectangleEditing();
    }

    /**
     * Selects the current map extent and emits it as a bounding box.
     */
    private selectByCurrentMapExtent(): void {
        const bounds = this.map.getBounds();

        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        const bbox = {
            xmin: sw.lng,
            ymin: sw.lat,
            xmax: ne.lng,
            ymax: ne.lat,
        };

        // Emit bbox to parent for OpenSearch filtering
        this.$emit("rectangle-drawn", bbox);


        // Fit the map to the new rectangle with some padding so it's fully visible
        this.map.fitBounds(bounds, { padding: [10, 10] });

        // Draw it on the map
        this.drawRectangle(bbox);
    }
}
